import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiCalendar, FiMapPin, FiArrowRight, FiClock } from "react-icons/fi";
import { eventAPI } from "../../services/api";
import { formatDate, resolveImageUrl } from "../../utils/helpers";
import { useDataRefresh } from "../../hooks/useDataRefresh";
import useScrollAnimation from "../../hooks/useScrollAnimation";

const fallbackEvents = [
  {
    _id: "1",
    title: "Annual Sports Day 2025",
    description:
      "Join us for a day of exciting sports competitions, team activities, and athletic excellence celebrating student talent.",
    date: "2025-03-15",
    location: "School Sports Complex",
    category: "Sports",
  },
  {
    _id: "2",
    title: "Science Exhibition & Fair",
    description:
      "Students showcase innovative science projects and experiments in our annual science exhibition open to parents and guests.",
    date: "2025-03-22",
    location: "School Auditorium",
    category: "Academic",
  },
  {
    _id: "3",
    title: "Parent-Teacher Meeting",
    description:
      "Quarterly meeting for parents to discuss academic progress, student development, and upcoming school activities.",
    date: "2025-04-05",
    location: "School Campus",
    category: "Meeting",
  },
];

const categoryColors = {
  Sports: "bg-primary-100 text-primary-700",
  Academic: "bg-secondary-100 text-secondary-700",
  Meeting: "bg-blue-100 text-blue-700",
  Cultural: "bg-pink-100 text-pink-700",
  Other: "bg-gray-100 text-gray-600",
};

export default function NewsEventsSection() {
  const { ref, isVisible } = useScrollAnimation();

  const { data: events = [] } = useDataRefresh(
    async () => {
      try {
        const res = await eventAPI.getAll();
        const data = res.data?.data?.events || [];
        return data.length > 0 ? data.slice(0, 3) : fallbackEvents;
      } catch {
        return fallbackEvents;
      }
    },
    "events",
    { pollInterval: 10000 }, // Refresh every 10 seconds
  );

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-12"
        >
          <div>
            <p className="section-subtitle mb-2">Stay Updated</p>
            <h2 className="section-title">News & Upcoming Events</h2>
          </div>
          <Link
            to="/news-events"
            className="mt-4 sm:mt-0 inline-flex items-center gap-2 text-primary-600 font-semibold hover:text-secondary-600 transition-colors"
          >
            View All Events <FiArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-6 lg:gap-8">
          {events.map((event, idx) => (
            <motion.article
              key={event._id || idx}
              initial={{ opacity: 0, y: 30 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: idx * 0.15 }}
              className="bg-white border border-gray-100 rounded-3xl overflow-hidden shadow-sm card-hover group"
            >
              <div className="h-44 bg-gradient-to-br from-primary-500 to-primary-800 relative overflow-hidden">
                {event.image?.url ? (
                  <img
                    src={resolveImageUrl(event.image.url)}
                    alt={event.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <FiCalendar className="w-16 h-16 text-white/20" />
                  </div>
                )}
                <div className="absolute top-4 left-4">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${categoryColors[event.category] || categoryColors.Other}`}
                  >
                    {event.category || "Event"}
                  </span>
                </div>
                <div className="absolute top-4 right-4 bg-white rounded-xl px-3 py-2 text-center shadow-md">
                  <div className="text-secondary-600 font-bold text-lg leading-none">
                    {event.date ? new Date(event.date).getDate() : ""}
                  </div>
                  <div className="text-gray-400 text-xs">
                    {event.date
                      ? new Date(event.date).toLocaleString("default", {
                          month: "short",
                        })
                      : ""}
                  </div>
                </div>
              </div>

              <div className="p-6">
                <h3 className="font-bold text-gray-800 text-lg mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors font-serif">
                  {event.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-3">
                  {event.description}
                </p>
                <div className="space-y-1.5">
                  {event.date && (
                    <div className="flex items-center gap-2 text-gray-400 text-xs">
                      <FiClock className="w-3.5 h-3.5 shrink-0" />
                      {formatDate(event.date)}
                    </div>
                  )}
                  {event.location && (
                    <div className="flex items-center gap-2 text-gray-400 text-xs">
                      <FiMapPin className="w-3.5 h-3.5 shrink-0" />
                      {event.location}
                    </div>
                  )}
                </div>
                <Link
                  to="/news-events"
                  className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary-600 hover:text-secondary-600 transition-colors"
                >
                  Read More <FiArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
